import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  Tooltip,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Spinner,
  Center,
  useToast,
  useColorModeValue,
  useDisclosure,
  Select,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Code,
  Tag,
  TagLabel,
  TagCloseButton,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { FiRefreshCw, FiUserPlus, FiSlash, FiLink } from 'react-icons/fi';
import { functionsUsEast1 } from '../../../utils/init-firebase';

/**
 * Admin > API Keys > AI Agent Keys > "ChatGPT Connector" section.
 *
 * Manages the OAuth side of the Origin Agent API:
 *   - which CRM users may link Origin in ChatGPT (users/{uid}.agentConnectorEnabled —
 *     checked at consent AND on every token use, so disabling cuts off
 *     outstanding tokens immediately)
 *   - active OAuth connections (Supabase crm_agent_connections) with revoke
 *
 * All data flows through the `manageAgentKeys` callable (us-east1), actions
 * connectorSetEnabled / connectorListConnections / connectorRevoke.
 */

const MCP_ENDPOINT = 'https://fhl.one/api/agent-mcp';

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function clientDisplayName(connection) {
  const client = connection.client || {};
  if (client.client_name) return client.client_name;
  const idUrl = String(client.client_id || '');
  if (idUrl.includes('chatgpt.com') || idUrl.includes('openai.com')) return 'ChatGPT';
  if (idUrl.includes('claude.ai') || idUrl.includes('claude.com')) return 'Claude';
  return client.registration_type === 'cimd' ? idUrl.replace(/^https?:\/\//, '').split('/')[0] : 'OAuth client';
}

export default function AgentConnectorPanel({ crmUsers = [] }) {
  const toast = useToast();

  // ---- theme values (all hooks before any early return) ----
  const cardBG = useColorModeValue('white', 'gray.800');
  const subText = useColorModeValue('gray.600', 'gray.400');
  const tableHeadBG = useColorModeValue('gray.50', 'gray.700');

  // ---- data state ----
  const [connections, setConnections] = useState([]);
  const [enabledUsers, setEnabledUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [addUserId, setAddUserId] = useState('');

  // ---- revoke dialog ----
  const revokeDialog = useDisclosure();
  const [revokeTarget, setRevokeTarget] = useState(null);
  const revokeCancelRef = useRef();

  // ---- disable dialog ----
  const disableDialog = useDisclosure();
  const [disableTarget, setDisableTarget] = useState(null);
  const disableCancelRef = useRef();

  const manageAgentKeys = useMemo(() => httpsCallable(functionsUsEast1, 'manageAgentKeys'), []);

  const load = useCallback(async () => {
    try {
      const res = await manageAgentKeys({ action: 'connectorListConnections' });
      setConnections(res.data?.connections || []);
      setEnabledUsers(res.data?.enabledUsers || []);
    } catch (err) {
      console.error('[AgentConnectorPanel] list error:', err);
      toast({ title: 'Failed to load connector data', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [manageAgentKeys, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const enabledIds = useMemo(() => new Set(enabledUsers.map((u) => u.id)), [enabledUsers]);
  const addableUsers = useMemo(
    () => crmUsers.filter((u) => !enabledIds.has(u.uid)),
    [crmUsers, enabledIds]
  );
  const activeConnections = useMemo(() => connections.filter((c) => c.status === 'active'), [connections]);

  const setEnabled = async (userId, enabled) => {
    setBusy(true);
    try {
      const res = await manageAgentKeys({ action: 'connectorSetEnabled', targetUserId: userId, enabled });
      const user = crmUsers.find((u) => u.uid === userId) || enabledUsers.find((u) => u.id === userId);
      const name = user?.name || userId;
      toast({
        title: enabled ? `${name} can now link Origin in ChatGPT` : `Connector disabled for ${name}`,
        description: enabled
          ? 'They approve access themselves on the Google-SSO consent screen the first time ChatGPT connects.'
          : `Their outstanding ChatGPT tokens stop working immediately${res.data?.revokedConnections ? ` (${res.data.revokedConnections} connection(s) revoked)` : ''}.`,
        status: enabled ? 'success' : 'warning',
        duration: 5000,
        isClosable: true,
      });
      setAddUserId('');
      await load();
    } catch (err) {
      console.error('[AgentConnectorPanel] setEnabled error:', err);
      toast({ title: 'Update failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  const openDisable = (user) => {
    setDisableTarget(user);
    disableDialog.onOpen();
  };

  const confirmDisable = async () => {
    if (!disableTarget) return;
    disableDialog.onClose();
    await setEnabled(disableTarget.id, false);
    setDisableTarget(null);
  };

  const openRevoke = (connection) => {
    setRevokeTarget(connection);
    revokeDialog.onOpen();
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setBusy(true);
    try {
      await manageAgentKeys({ action: 'connectorRevoke', id: revokeTarget.id });
      revokeDialog.onClose();
      toast({
        title: 'Connection revoked',
        description: `${revokeTarget.user_name || revokeTarget.user_email || 'The user'} must re-link Origin in ChatGPT to reconnect.`,
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
      await load();
    } catch (err) {
      console.error('[AgentConnectorPanel] revoke error:', err);
      toast({ title: 'Revoke failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setBusy(false);
      setRevokeTarget(null);
    }
  };

  if (loading) {
    return (
      <Center py={10}>
        <Spinner size="lg" color="purple.500" thickness="3px" />
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      {/* Allowed users */}
      <Box bg={cardBG} p={6} borderRadius="xl" shadow="sm">
        <HStack justify="space-between" align="flex-start" mb={1}>
          <VStack align="flex-start" spacing={1}>
            <HStack>
              <FiLink />
              <Text fontWeight="bold" fontSize="lg">ChatGPT Connector</Text>
            </HStack>
            <Text color={subText} fontSize="sm" maxW="640px">
              The native Origin connector for the ChatGPT Business workspace. Unlike agent keys, users
              sign in with their own FHL Google account and approve access themselves — but only users
              enabled here can complete that consent screen. Disabling a user cuts off their existing
              ChatGPT connection immediately.
            </Text>
          </VStack>
          <Tooltip label="Refresh">
            <IconButton icon={<FiRefreshCw />} aria-label="Refresh connector data" variant="ghost" onClick={load} isDisabled={busy} />
          </Tooltip>
        </HStack>

        <HStack mt={4} spacing={3} maxW="520px">
          <Select
            placeholder="Add a CRM user…"
            size="sm"
            value={addUserId}
            onChange={(e) => setAddUserId(e.target.value)}
          >
            {addableUsers.map((u) => (
              <option key={u.uid} value={u.uid}>
                {u.name}{u.email ? ` — ${u.email}` : ''}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            colorScheme="purple"
            leftIcon={<FiUserPlus />}
            onClick={() => addUserId && setEnabled(addUserId, true)}
            isDisabled={!addUserId || busy}
            flexShrink={0}
          >
            Allow
          </Button>
        </HStack>

        <Wrap mt={4} spacing={2}>
          {enabledUsers.length === 0 && (
            <Text fontSize="sm" color={subText}>
              No users enabled yet — nobody can link Origin in ChatGPT until you allow them here.
            </Text>
          )}
          {enabledUsers.map((u) => (
            <WrapItem key={u.id}>
              <Tag size="md" borderRadius="full" colorScheme="purple" variant="subtle">
                <TagLabel>{u.name || u.email || u.id}</TagLabel>
                <TagCloseButton onClick={() => openDisable(u)} isDisabled={busy} aria-label={`Disable connector for ${u.name || u.id}`} />
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      </Box>

      {/* Connections table */}
      <Box bg={cardBG} borderRadius="xl" shadow="sm" overflow="hidden">
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg={tableHeadBG}>
              <Tr>
                <Th>User</Th>
                <Th>App</Th>
                <Th>Status</Th>
                <Th>Scopes</Th>
                <Th>Connected</Th>
                <Th>Last Used</Th>
                <Th isNumeric>Requests</Th>
                <Th width="1%" />
              </Tr>
            </Thead>
            <Tbody>
              {connections.length === 0 && (
                <Tr>
                  <Td colSpan={8}>
                    <Center py={8}>
                      <Text color={subText} fontSize="sm">
                        No connections yet. They appear here after a user links Origin in ChatGPT.
                      </Text>
                    </Center>
                  </Td>
                </Tr>
              )}
              {connections.map((c) => (
                <Tr key={c.id}>
                  <Td>
                    <VStack align="flex-start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium">{c.user_name || c.user_id}</Text>
                      {c.user_email && <Text fontSize="xs" color={subText}>{c.user_email}</Text>}
                    </VStack>
                  </Td>
                  <Td>
                    <Badge colorScheme="blue">{clientDisplayName(c)}</Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={c.status === 'active' ? 'green' : 'red'}>{c.status}</Badge>
                  </Td>
                  <Td>
                    <Tooltip label={(Array.isArray(c.scopes) ? c.scopes : []).join(', ') || 'none'} maxW="320px">
                      <Badge colorScheme="gray">{Array.isArray(c.scopes) ? c.scopes.length : 0} scopes</Badge>
                    </Tooltip>
                  </Td>
                  <Td><Text fontSize="xs">{formatDateTime(c.created_at)}</Text></Td>
                  <Td><Text fontSize="xs">{formatDateTime(c.last_used_at)}</Text></Td>
                  <Td isNumeric>{Number(c.usage_count || 0).toLocaleString()}</Td>
                  <Td>
                    {c.status === 'active' && (
                      <Tooltip label="Revoke this connection (all its tokens stop working)">
                        <IconButton
                          icon={<FiSlash />}
                          aria-label={`Revoke connection for ${c.user_name || c.user_id}`}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => openRevoke(c)}
                          isDisabled={busy}
                        />
                      </Tooltip>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {/* Setup info */}
      <Alert status="info" variant="left-accent" borderRadius="lg" alignItems="flex-start">
        <AlertIcon />
        <Box>
          <AlertTitle fontSize="sm">Adding Origin to the ChatGPT Business workspace</AlertTitle>
          <AlertDescription fontSize="sm">
            <VStack align="flex-start" spacing={1} mt={2}>
              <HStack spacing={2}>
                <Badge>MCP Server URL</Badge>
                <Code fontSize="xs">{MCP_ENDPOINT}</Code>
              </HStack>
              <Text pt={1}>
                Workspace admin: ChatGPT → Settings → <b>Connectors</b> → <b>Create</b> → paste the URL
                above with <b>OAuth</b> authentication (discovery is automatic). Each user then clicks{' '}
                <b>Connect</b> and signs in with their FHL Google account. {activeConnections.length} active
                connection{activeConnections.length === 1 ? '' : 's'} right now. Full runbook:{' '}
                <Code fontSize="xs">docs/chatgpt-connector-admin-guide.md</Code>.
              </Text>
            </VStack>
          </AlertDescription>
        </Box>
      </Alert>

      {/* Revoke confirm */}
      <AlertDialog isOpen={revokeDialog.isOpen} leastDestructiveRef={revokeCancelRef} onClose={revokeDialog.onClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Revoke this connection?</AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              <Text>
                <b>{revokeTarget?.user_name || revokeTarget?.user_email || 'This user'}</b>'s{' '}
                {revokeTarget ? clientDisplayName(revokeTarget) : ''} connection and all of its tokens stop
                working immediately. They stay on the allowed list and can re-link Origin in ChatGPT at any time.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={revokeCancelRef} variant="ghost" onClick={revokeDialog.onClose} isDisabled={busy}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmRevoke} ml={3} isLoading={busy} leftIcon={<FiSlash />}>
                Revoke
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Disable confirm */}
      <AlertDialog isOpen={disableDialog.isOpen} leastDestructiveRef={disableCancelRef} onClose={disableDialog.onClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Disable connector for {disableTarget?.name || disableTarget?.email || 'this user'}?</AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              <Text>
                Their active ChatGPT connections are revoked and every outstanding token stops working
                immediately. They will not be able to re-link Origin until you allow them again.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={disableCancelRef} variant="ghost" onClick={disableDialog.onClose} isDisabled={busy}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDisable} ml={3} isLoading={busy}>
                Disable
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
}
